import * as React from 'react'
import { useEffect } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  TableMeta,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onSearch?: (value: string) => void
  onSort?: (field: string, order: 'asc' | 'desc') => void
  initialSort?: { id: string; desc: boolean }
  meta?: TableMeta<TData>
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onSearch,
  onSort,
  initialSort,
  meta,
}: DataTableProps<TData, TValue>) {
  const [searchValue, setSearchValue] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>(
    initialSort ? [initialSort] : [{ id: 'created_at', desc: true }],
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      setSorting(newSorting)

      if (onSort && newSorting.length > 0) {
        const { id, desc } = newSorting[0]
        if (id === 'created_at') {
          onSort(id, desc ? 'desc' : 'asc')
        }
      }
    },
    state: {
      sorting,
    },
    meta,
  })

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch?.(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue, onSearch])

  return (
    <div>
      <div className="flex items-center py-4">
        <Input
          placeholder="搜尋廣告標題..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  無資料
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
